/**
 * Typed, allowlisted client for Microsoft's preview Canvas Authoring MCP server.
 *
 * This is deliberately not a generic MCP proxy. Only the nine authoring tools
 * whose contracts are validated below can be invoked through this class.
 */

import path from "node:path";
import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { StdioClientTransport } from "@modelcontextprotocol/sdk/client/stdio.js";
import type { CallToolResult } from "@modelcontextprotocol/sdk/types.js";

export const CANVAS_AUTHORING_PACKAGE =
  "Microsoft.PowerApps.CanvasAuthoring.McpServer@1.0.3483.70-preview";
export const CANVAS_AUTHORING_COMMAND = "dnx";
export const CANVAS_AUTHORING_ARGS = Object.freeze([
  CANVAS_AUTHORING_PACKAGE,
  "--yes",
  "--source",
  "https://api.nuget.org/v3/index.json",
]);

export const CANVAS_AUTHORING_TOOL_NAMES = Object.freeze([
  "connect",
  "compile_canvas",
  "describe_api",
  "describe_control",
  "get_data_source_schema",
  "list_apis",
  "list_controls",
  "list_data_sources",
  "sync_canvas",
] as const);

export type CanvasAuthoringToolName = (typeof CANVAS_AUTHORING_TOOL_NAMES)[number];
export type CanvasEnvironmentCategory =
  "test" | "preprod" | "prod" | "gov" | "high" | "dod" | "china";
export type CanvasAuthFlow = "broker" | "browser" | "devicecode";

export interface CanvasConnectInput {
  environmentId: string;
  appId: string;
  environmentCategory?: CanvasEnvironmentCategory;
  authFlow?: CanvasAuthFlow;
  loginHint?: string;
  tenantId?: string;
  forceAccountSelect?: boolean;
}

export type CanvasAuthoringToolResult = CallToolResult;

export interface CanvasAuthoringClientLike {
  connect(input: CanvasConnectInput): Promise<CanvasAuthoringToolResult>;
  listControls(): Promise<CanvasAuthoringToolResult>;
  describeControl(controlName: string): Promise<CanvasAuthoringToolResult>;
  listApis(): Promise<CanvasAuthoringToolResult>;
  describeApi(apiName: string): Promise<CanvasAuthoringToolResult>;
  listDataSources(): Promise<CanvasAuthoringToolResult>;
  getDataSourceSchema(dataSourceName: string): Promise<CanvasAuthoringToolResult>;
  syncCanvas(directoryPath: string): Promise<CanvasAuthoringToolResult>;
  compileCanvas(directoryPath: string): Promise<CanvasAuthoringToolResult>;
  close(): Promise<void>;
}

export type CanvasAuthoringErrorCategory =
  | "validation"
  | "closed"
  | "startup"
  | "contract"
  | "timeout"
  | "transport"
  | "runtime-unavailable"
  | "package-unavailable"
  | "consent-missing"
  | "tenant-mismatch"
  | "unauthenticated"
  | "forbidden"
  | "app-not-found"
  | "coauthoring-unavailable"
  | "session-inactive"
  | "not-connected"
  | "validation-failed"
  | "write-outcome-unknown"
  | "data-source-not-found"
  | "tool";

const ERROR_CODES: Record<CanvasAuthoringErrorCategory, string> = {
  validation: "CANVAS_VALIDATION_ERROR",
  closed: "CANVAS_CLIENT_CLOSED",
  startup: "CANVAS_STARTUP_ERROR",
  contract: "CANVAS_CONTRACT_ERROR",
  timeout: "CANVAS_TIMEOUT_ERROR",
  transport: "CANVAS_TRANSPORT_ERROR",
  "runtime-unavailable": "CANVAS_RUNTIME_UNAVAILABLE",
  "package-unavailable": "CANVAS_PACKAGE_UNAVAILABLE",
  "consent-missing": "CANVAS_CONSENT_MISSING",
  "tenant-mismatch": "CANVAS_TENANT_MISMATCH",
  unauthenticated: "CANVAS_UNAUTHENTICATED",
  forbidden: "CANVAS_FORBIDDEN",
  "app-not-found": "CANVAS_APP_NOT_FOUND",
  "coauthoring-unavailable": "CANVAS_COAUTHORING_UNAVAILABLE",
  "session-inactive": "CANVAS_SESSION_INACTIVE",
  "not-connected": "CANVAS_NOT_CONNECTED",
  "validation-failed": "CANVAS_VALIDATION_FAILED",
  "write-outcome-unknown": "CANVAS_WRITE_OUTCOME_UNKNOWN",
  "data-source-not-found": "CANVAS_DATA_SOURCE_NOT_FOUND",
  tool: "CANVAS_TOOL_ERROR",
};

interface CanvasAuthoringErrorOptions {
  toolName?: CanvasAuthoringToolName;
  diagnostic?: string;
}

/** Stable, sanitized error surface for callers of the preview child server. */
export class CanvasAuthoringError extends Error {
  readonly code: string;
  readonly toolName?: CanvasAuthoringToolName;
  readonly diagnostic?: string;

  constructor(
    message: string,
    readonly category: CanvasAuthoringErrorCategory,
    options: CanvasAuthoringErrorOptions = {},
  ) {
    super(message);
    this.name = "CanvasAuthoringError";
    this.code = ERROR_CODES[category];
    this.toolName = options.toolName;
    this.diagnostic = options.diagnostic ? sanitizeDiagnostic(options.diagnostic) : undefined;
    Error.captureStackTrace(this, this.constructor);
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      category: this.category,
      toolName: this.toolName,
      diagnostic: this.diagnostic,
    };
  }
}

export interface CanvasAuthoringClientOptions {
  /** Child initialization and tools/list contract-check timeout. */
  startupTimeoutMs?: number;
  /** connect can open an interactive browser or broker UI. */
  connectTimeoutMs?: number;
  /** Metadata/discovery call timeout. */
  discoveryTimeoutMs?: number;
  /** sync_canvas and compile_canvas timeout. */
  mutationTimeoutMs?: number;
  /** Maximum accepted serialized MCP tool result. */
  maxResultBytes?: number;
}

interface ResolvedOptions {
  startupTimeoutMs: number;
  connectTimeoutMs: number;
  discoveryTimeoutMs: number;
  mutationTimeoutMs: number;
  maxResultBytes: number;
}

interface ExpectedProperty {
  type: "string" | "boolean";
  defaultValue?: unknown;
}

interface ToolContract {
  required: readonly string[];
  properties: Readonly<Record<string, ExpectedProperty>>;
}

const ENVIRONMENT_CATEGORIES = Object.freeze([
  "test",
  "preprod",
  "prod",
  "gov",
  "high",
  "dod",
  "china",
] as const);
const AUTH_FLOWS = Object.freeze(["broker", "browser", "devicecode"] as const);
const CALLABLE_AUTH_FLOWS = new Set<CanvasAuthFlow>(["broker", "browser"]);
const KNOWN_UNEXPOSED_TOOLS = new Set(["list_accounts", "remove_account"]);

const TOOL_CONTRACTS: Record<CanvasAuthoringToolName, ToolContract> = {
  connect: {
    required: ["environment_id", "app_id"],
    properties: {
      environment_id: { type: "string" },
      app_id: { type: "string" },
      environment_category: { type: "string", defaultValue: "" },
      auth_flow: { type: "string", defaultValue: "" },
      login_hint: { type: "string", defaultValue: "" },
      tenant_id: { type: "string", defaultValue: "" },
      force_account_select: { type: "boolean", defaultValue: false },
    },
  },
  compile_canvas: {
    required: ["directoryPath"],
    properties: { directoryPath: { type: "string" } },
  },
  describe_api: {
    required: ["apiName"],
    properties: { apiName: { type: "string" } },
  },
  describe_control: {
    required: ["controlName"],
    properties: { controlName: { type: "string" } },
  },
  get_data_source_schema: {
    required: ["dataSourceName"],
    properties: { dataSourceName: { type: "string" } },
  },
  list_apis: { required: [], properties: {} },
  list_controls: { required: [], properties: {} },
  list_data_sources: { required: [], properties: {} },
  sync_canvas: {
    required: ["directoryPath"],
    properties: { directoryPath: { type: "string" } },
  },
};

const DEFAULT_OPTIONS: ResolvedOptions = {
  startupTimeoutMs: 120_000,
  connectTimeoutMs: 300_000,
  discoveryTimeoutMs: 60_000,
  mutationTimeoutMs: 300_000,
  maxResultBytes: 8 * 1024 * 1024,
};
const MAX_STDERR_CHARS = 8_192;
const MAX_DIAGNOSTIC_CHARS = 2_048;
const MAX_TOOL_LIST_PAGES = 8;
const MAX_ADVERTISED_TOOLS = 32;
const MCP_CONNECTION_CLOSED = -32_000;
const MCP_REQUEST_TIMEOUT = -32_001;
const ANSI_ESCAPE = new RegExp(`${String.fromCharCode(27)}\\[[0-?]*[ -/]*[@-~]`, "g");

/**
 * Production client for the pinned Canvas Authoring preview MCP child.
 * Calls and startup are serialized because the child holds mutable app state.
 */
export class CanvasAuthoringClient implements CanvasAuthoringClientLike {
  private readonly options: ResolvedOptions;
  private client?: Client;
  private transport?: StdioClientTransport;
  private stderrTail = "";
  private stderrListener?: (chunk: unknown) => void;
  private queue: Promise<void> = Promise.resolve();
  private closePromise?: Promise<void>;
  private closeRequested = false;

  constructor(options: CanvasAuthoringClientOptions = {}) {
    this.options = {
      startupTimeoutMs: positiveInteger(
        options.startupTimeoutMs,
        DEFAULT_OPTIONS.startupTimeoutMs,
        "startupTimeoutMs",
      ),
      connectTimeoutMs: positiveInteger(
        options.connectTimeoutMs,
        DEFAULT_OPTIONS.connectTimeoutMs,
        "connectTimeoutMs",
      ),
      discoveryTimeoutMs: positiveInteger(
        options.discoveryTimeoutMs,
        DEFAULT_OPTIONS.discoveryTimeoutMs,
        "discoveryTimeoutMs",
      ),
      mutationTimeoutMs: positiveInteger(
        options.mutationTimeoutMs,
        DEFAULT_OPTIONS.mutationTimeoutMs,
        "mutationTimeoutMs",
      ),
      maxResultBytes: positiveInteger(
        options.maxResultBytes,
        DEFAULT_OPTIONS.maxResultBytes,
        "maxResultBytes",
      ),
    };
  }

  connect(input: CanvasConnectInput): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall("connect", normalizeConnectInput(input), this.options.connectTimeoutMs),
    );
  }

  listControls(): Promise<CanvasAuthoringToolResult> {
    return this.scheduleToolCall("list_controls", {}, this.options.discoveryTimeoutMs);
  }

  describeControl(controlName: string): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall(
        "describe_control",
        { controlName: nonEmptyString(controlName, "controlName") },
        this.options.discoveryTimeoutMs,
      ),
    );
  }

  listApis(): Promise<CanvasAuthoringToolResult> {
    return this.scheduleToolCall("list_apis", {}, this.options.discoveryTimeoutMs);
  }

  describeApi(apiName: string): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall(
        "describe_api",
        { apiName: nonEmptyString(apiName, "apiName") },
        this.options.discoveryTimeoutMs,
      ),
    );
  }

  listDataSources(): Promise<CanvasAuthoringToolResult> {
    return this.scheduleToolCall("list_data_sources", {}, this.options.discoveryTimeoutMs);
  }

  getDataSourceSchema(dataSourceName: string): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall(
        "get_data_source_schema",
        { dataSourceName: nonEmptyString(dataSourceName, "dataSourceName") },
        this.options.discoveryTimeoutMs,
      ),
    );
  }

  syncCanvas(directoryPath: string): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall(
        "sync_canvas",
        { directoryPath: absoluteDirectoryPath(directoryPath) },
        this.options.mutationTimeoutMs,
      ),
    );
  }

  compileCanvas(directoryPath: string): Promise<CanvasAuthoringToolResult> {
    return promiseFrom(() =>
      this.scheduleToolCall(
        "compile_canvas",
        { directoryPath: absoluteDirectoryPath(directoryPath) },
        this.options.mutationTimeoutMs,
      ),
    );
  }

  close(): Promise<void> {
    if (this.closePromise) return this.closePromise;

    this.closeRequested = true;
    this.closePromise = this.enqueue(async () => {
      const closeError = await this.disposeCurrentConnection();
      if (closeError !== undefined) {
        throw new CanvasAuthoringError(
          "The Canvas authoring MCP child could not be closed cleanly.",
          "transport",
          { diagnostic: errorDiagnostic(closeError) },
        );
      }
    });
    return this.closePromise;
  }

  private scheduleToolCall(
    toolName: CanvasAuthoringToolName,
    args: Record<string, unknown>,
    timeoutMs: number,
  ): Promise<CanvasAuthoringToolResult> {
    if (this.closeRequested) {
      return Promise.reject(
        new CanvasAuthoringError("The Canvas authoring client has already been closed.", "closed", {
          toolName,
        }),
      );
    }

    return this.enqueue(() => this.callTool(toolName, args, timeoutMs));
  }

  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.queue.then(operation, operation);
    this.queue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private async callTool(
    toolName: CanvasAuthoringToolName,
    args: Record<string, unknown>,
    timeoutMs: number,
  ): Promise<CanvasAuthoringToolResult> {
    const client = await this.ensureStarted();

    try {
      const rawResult = await client.callTool(
        { name: toolName, arguments: args },
        undefined,
        requestOptions(timeoutMs),
      );
      const result = validateToolResult(rawResult, toolName, this.options.maxResultBytes);
      const diagnostic = resultDiagnostic(result);
      const payloadFailure = classifySuccessfulPayloadFailure(toolName, diagnostic);

      if (result.isError || payloadFailure) {
        throw classifiedToolError(
          toolName,
          payloadFailure ?? classifyToolDiagnostic(toolName, diagnostic),
          diagnostic,
        );
      }

      return result;
    } catch (error) {
      if (error instanceof CanvasAuthoringError) {
        if (error.category === "contract" || error.category === "write-outcome-unknown") {
          await this.disposeCurrentConnection();
        }
        throw error;
      }

      const diagnostic = this.combinedDiagnostic(error);
      const category = classifyCallFailure(error, toolName, diagnostic);
      if (
        category === "timeout" ||
        category === "transport" ||
        category === "write-outcome-unknown"
      ) {
        await this.disposeCurrentConnection();
      }
      throw classifiedToolError(toolName, category, diagnostic);
    }
  }

  private async ensureStarted(): Promise<Client> {
    if (this.client) return this.client;

    this.stderrTail = "";
    const transport = new StdioClientTransport({
      command: CANVAS_AUTHORING_COMMAND,
      args: [...CANVAS_AUTHORING_ARGS],
      stderr: "pipe",
    });
    this.attachStderr(transport);

    const client = new Client(
      { name: "powerautomate-mcp-canvas-bridge", version: "1.0.0" },
      { capabilities: {} },
    );

    try {
      await client.connect(transport, requestOptions(this.options.startupTimeoutMs));
      await validateAdvertisedTools(client, this.options.startupTimeoutMs);
      this.client = client;
      this.transport = transport;
      return client;
    } catch (error) {
      const diagnostic = this.combinedDiagnostic(error);
      await this.disposePair(client, transport);

      if (error instanceof CanvasAuthoringError) throw error;
      const category = classifyStartupFailure(error, diagnostic);
      throw new CanvasAuthoringError(categoryMessage(category), category, { diagnostic });
    }
  }

  private attachStderr(transport: StdioClientTransport): void {
    const stderr = transport.stderr;
    if (!stderr) return;

    this.stderrListener = (chunk: unknown): void => {
      if (typeof chunk !== "string" && !Buffer.isBuffer(chunk)) return;
      const sanitized = sanitizeDiagnostic(chunk.toString());
      this.stderrTail = `${this.stderrTail}${sanitized}`.slice(-MAX_STDERR_CHARS);
    };
    stderr.on("data", this.stderrListener);
  }

  private detachStderr(transport: StdioClientTransport): void {
    if (!this.stderrListener) return;
    transport.stderr?.removeListener("data", this.stderrListener);
    this.stderrListener = undefined;
  }

  private combinedDiagnostic(error: unknown): string | undefined {
    const parts = [errorDiagnostic(error), this.stderrTail]
      .filter((part): part is string => Boolean(part))
      .join("\n");
    return parts ? sanitizeDiagnostic(parts) : undefined;
  }

  private async disposeCurrentConnection(): Promise<unknown> {
    const client = this.client;
    const transport = this.transport;
    this.client = undefined;
    this.transport = undefined;
    if (!client && !transport) return undefined;
    return this.disposePair(client, transport);
  }

  private async disposePair(
    client: Client | undefined,
    transport: StdioClientTransport | undefined,
  ): Promise<unknown> {
    if (transport) this.detachStderr(transport);

    let closeError: unknown;
    if (client) {
      try {
        await client.close();
        return undefined;
      } catch (error) {
        closeError = error;
      }
    }

    if (transport) {
      try {
        await transport.close();
      } catch (error) {
        closeError ??= error;
      }
    }
    return closeError;
  }
}

function promiseFrom<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return operation();
  } catch (error) {
    return Promise.reject(
      error instanceof Error
        ? error
        : new CanvasAuthoringError("Canvas authoring input validation failed.", "validation"),
    );
  }
}

function positiveInteger(value: number | undefined, fallback: number, name: string): number {
  if (value === undefined) return fallback;
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new CanvasAuthoringError(`${name} must be a positive integer.`, "validation");
  }
  return value;
}

function nonEmptyString(value: string, name: string): string {
  if (typeof value !== "string" || value.trim().length === 0 || value.includes("\0")) {
    throw new CanvasAuthoringError(`${name} must be a non-empty string.`, "validation");
  }
  return value.trim();
}

function absoluteDirectoryPath(value: string): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.includes("\0") ||
    !path.isAbsolute(value)
  ) {
    throw new CanvasAuthoringError("directoryPath must be an absolute path.", "validation");
  }
  return value;
}

function normalizeConnectInput(input: CanvasConnectInput): Record<string, unknown> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new CanvasAuthoringError("Canvas connect input must be an object.", "validation");
  }

  const environmentId = nonEmptyString(input.environmentId, "environmentId");
  const appId = nonEmptyString(input.appId, "appId");
  const args: Record<string, unknown> = {
    environment_id: environmentId,
    app_id: appId,
  };

  if (input.environmentCategory !== undefined) {
    if (!ENVIRONMENT_CATEGORIES.includes(input.environmentCategory)) {
      throw new CanvasAuthoringError(
        "environmentCategory is not supported by the pinned Canvas authoring server.",
        "validation",
      );
    }
    args["environment_category"] = input.environmentCategory;
  }

  if (input.authFlow !== undefined) {
    if (!AUTH_FLOWS.includes(input.authFlow)) {
      throw new CanvasAuthoringError(
        "authFlow is not supported by the pinned Canvas authoring server.",
        "validation",
      );
    }
    if (!CALLABLE_AUTH_FLOWS.has(input.authFlow)) {
      throw new CanvasAuthoringError(
        "authFlow 'devicecode' is unavailable because this bridge cannot safely proxy MCP elicitation; use 'broker' or 'browser'.",
        "validation",
      );
    }
    args["auth_flow"] = input.authFlow;
  }

  if (input.loginHint !== undefined) {
    args["login_hint"] = nonEmptyString(input.loginHint, "loginHint");
  }
  if (input.tenantId !== undefined) {
    args["tenant_id"] = nonEmptyString(input.tenantId, "tenantId");
  }
  if (input.forceAccountSelect !== undefined) {
    if (typeof input.forceAccountSelect !== "boolean") {
      throw new CanvasAuthoringError("forceAccountSelect must be a boolean.", "validation");
    }
    args["force_account_select"] = input.forceAccountSelect;
  }

  return args;
}

async function validateAdvertisedTools(client: Client, timeoutMs: number): Promise<void> {
  const advertised = new Map<string, Awaited<ReturnType<Client["listTools"]>>["tools"][number]>();
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  for (let pageNumber = 0; pageNumber < MAX_TOOL_LIST_PAGES; pageNumber += 1) {
    const page = await client.listTools(cursor ? { cursor } : undefined, requestOptions(timeoutMs));
    for (const tool of page.tools) {
      if (advertised.has(tool.name)) {
        throw contractError(`Canvas child advertised duplicate tool "${tool.name}".`);
      }
      advertised.set(tool.name, tool);
      if (advertised.size > MAX_ADVERTISED_TOOLS) {
        throw contractError("Canvas child advertised too many tools.");
      }
    }

    cursor = page.nextCursor;
    if (!cursor) break;
    if (seenCursors.has(cursor)) {
      throw contractError("Canvas child repeated a tools/list cursor.");
    }
    seenCursors.add(cursor);

    if (pageNumber === MAX_TOOL_LIST_PAGES - 1) {
      throw contractError("Canvas child tools/list exceeded the page limit.");
    }
  }

  for (const name of advertised.keys()) {
    if (
      !CANVAS_AUTHORING_TOOL_NAMES.includes(name as CanvasAuthoringToolName) &&
      !KNOWN_UNEXPOSED_TOOLS.has(name)
    ) {
      throw contractError(`Canvas child advertised unknown tool "${name}".`);
    }
  }

  for (const toolName of CANVAS_AUTHORING_TOOL_NAMES) {
    const tool = advertised.get(toolName);
    if (!tool) {
      throw contractError(`Canvas child is missing required tool "${toolName}".`);
    }
    validateInputSchema(toolName, tool.inputSchema, TOOL_CONTRACTS[toolName]);
  }
}

function validateInputSchema(
  toolName: CanvasAuthoringToolName,
  schema: Record<string, unknown>,
  contract: ToolContract,
): void {
  if (schema["type"] !== "object") {
    throw contractError(`Tool "${toolName}" no longer has an object input schema.`);
  }

  const actualRequired = schema["required"] ?? [];
  if (
    !Array.isArray(actualRequired) ||
    actualRequired.some((entry) => typeof entry !== "string") ||
    !sameStringSet(actualRequired as string[], contract.required)
  ) {
    throw contractError(`Tool "${toolName}" required inputs changed.`);
  }

  const properties = asRecord(schema["properties"]);
  if (!properties && Object.keys(contract.properties).length > 0) {
    throw contractError(`Tool "${toolName}" input properties are missing.`);
  }

  for (const [propertyName, expected] of Object.entries(contract.properties)) {
    const property = asRecord(properties?.[propertyName]);
    if (!property || property["type"] !== expected.type) {
      throw contractError(`Tool "${toolName}" input "${propertyName}" changed type.`);
    }

    if (Object.hasOwn(expected, "defaultValue") && property["default"] !== expected.defaultValue) {
      throw contractError(`Tool "${toolName}" input "${propertyName}" changed its default.`);
    }
  }
}

function sameStringSet(actual: readonly string[], expected: readonly string[]): boolean {
  return (
    actual.length === expected.length &&
    new Set(actual).size === actual.length &&
    actual.every((entry) => expected.includes(entry))
  );
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function contractError(message: string): CanvasAuthoringError {
  return new CanvasAuthoringError(message, "contract");
}

function validateToolResult(
  result: unknown,
  toolName: CanvasAuthoringToolName,
  maxResultBytes: number,
): CanvasAuthoringToolResult {
  const record = asRecord(result);
  if (!record || !Array.isArray(record["content"])) {
    throw new CanvasAuthoringError(
      `Canvas authoring tool "${toolName}" returned an incompatible result.`,
      "contract",
      { toolName },
    );
  }

  let serialized: string;
  try {
    serialized = JSON.stringify(result);
  } catch {
    throw new CanvasAuthoringError(
      `Canvas authoring tool "${toolName}" returned an unserializable result.`,
      "contract",
      { toolName },
    );
  }
  if (Buffer.byteLength(serialized, "utf8") > maxResultBytes) {
    throw new CanvasAuthoringError(
      `Canvas authoring tool "${toolName}" result exceeded the size limit.`,
      "contract",
      { toolName },
    );
  }

  return result as CanvasAuthoringToolResult;
}

function requestOptions(timeoutMs: number): {
  timeout: number;
  maxTotalTimeout: number;
} {
  return { timeout: timeoutMs, maxTotalTimeout: timeoutMs };
}

function classifyStartupFailure(
  error: unknown,
  diagnostic: string | undefined,
): CanvasAuthoringErrorCategory {
  if (isTimeoutFailure(error)) return "timeout";

  const normalized = diagnostic?.toLowerCase() ?? "";
  if (
    /(?:\benoent\b|spawn\s+dnx|dnx[^\n]*(?:not found|unavailable)|dotnet[^\n]*(?:not found|unavailable)|\.net sdk[^\n]*(?:not found|required|missing)|no executable found|command not found)/iu.test(
      normalized,
    )
  ) {
    return "runtime-unavailable";
  }
  if (
    /(?:\bnu1101\b|package[^\n]*(?:not found|unavailable)|unable to (?:find|download|restore)[^\n]*package|failed to (?:download|restore)|nuget[^\n]*(?:unavailable|failed)|service index|no versions? available)/iu.test(
      normalized,
    )
  ) {
    return "package-unavailable";
  }
  return "startup";
}

function classifyCallFailure(
  error: unknown,
  toolName: CanvasAuthoringToolName,
  diagnostic: string | undefined,
): CanvasAuthoringErrorCategory {
  const baseCategory: CanvasAuthoringErrorCategory = isTimeoutFailure(error)
    ? "timeout"
    : errorCode(error) === MCP_CONNECTION_CLOSED || errorCode(error) === undefined
      ? "transport"
      : "tool";

  if (
    toolName === "compile_canvas" &&
    (baseCategory === "timeout" ||
      baseCategory === "transport" ||
      isTransientWriteFailure(diagnostic))
  ) {
    return "write-outcome-unknown";
  }

  const specificCategory = classifyToolDiagnostic(toolName, diagnostic);
  return specificCategory === "tool" ? baseCategory : specificCategory;
}

function classifySuccessfulPayloadFailure(
  toolName: CanvasAuthoringToolName,
  diagnostic: string | undefined,
): CanvasAuthoringErrorCategory | undefined {
  if (!diagnostic || (toolName !== "compile_canvas" && toolName !== "sync_canvas")) {
    return undefined;
  }

  if (
    toolName === "sync_canvas" &&
    /no files returned from (?:the )?server[^\n]*nothing written/iu.test(diagnostic)
  ) {
    return "session-inactive";
  }

  const classified = classifyToolDiagnostic(toolName, diagnostic);
  if (classified !== "tool") {
    return classified;
  }

  if (
    toolName === "compile_canvas" &&
    (/(?:validation\s+failed|errors?\s*[:=]\s*[1-9]\d*|[1-9]\d*\s+errors?\b)/iu.test(diagnostic) ||
      /(?:warnings?\s*[:=]\s*[1-9]\d*|[1-9]\d*\s+warnings?\b)/iu.test(diagnostic))
  ) {
    return "validation-failed";
  }

  return undefined;
}

function classifyToolDiagnostic(
  toolName: CanvasAuthoringToolName,
  diagnostic: string | undefined,
): CanvasAuthoringErrorCategory {
  const normalized = diagnostic?.toLowerCase() ?? "";
  if (!normalized) return "tool";

  if (toolName === "compile_canvas" && isTransientWriteFailure(normalized)) {
    return "write-outcome-unknown";
  }
  if (
    /(?:data\s*source[^\n]*(?:not found|does not exist|unavailable)|could not find[^\n]*data\s*source)/iu.test(
      normalized,
    )
  ) {
    return "data-source-not-found";
  }
  if (
    /(?:\baadsts65001\b|admin consent|consent[^\n]*(?:required|missing|not granted)|permission grant[^\n]*missing)/iu.test(
      normalized,
    )
  ) {
    return "consent-missing";
  }
  if (
    /(?:\baadsts50020\b|tenant mismatch|wrong tenant|different tenant|does not exist in (?:the )?tenant|account[^\n]*tenant[^\n]*(?:not|different))/iu.test(
      normalized,
    )
  ) {
    return "tenant-mismatch";
  }
  if (
    /(?:\baadsts50058\b|\b401\b|not authenticated|authentication[^\n]*(?:failed|required)|sign[ -]?in[^\n]*required|no cached account|credential[^\n]*unavailable|\bunauthorized\b)/iu.test(
      normalized,
    )
  ) {
    return "unauthenticated";
  }
  if (
    /(?:app(?:lication)?[^\n]*(?:not found|does not exist)|(?:not found|404)[^\n]*app(?:lication)?)/iu.test(
      normalized,
    )
  ) {
    return "app-not-found";
  }
  if (
    /(?:co-?authoring[^\n]*(?:disabled|not enabled|unavailable|unsupported)|enable[^\n]*co-?authoring)/iu.test(
      normalized,
    )
  ) {
    return "coauthoring-unavailable";
  }
  if (
    /(?:studio[^\n]*(?:not open|closed)|session[^\n]*(?:inactive|expired|not active)|keep[^\n]*studio[^\n]*open|live session[^\n]*(?:missing|unavailable|not))/iu.test(
      normalized,
    )
  ) {
    return "session-inactive";
  }
  if (
    /(?:not connected|connect(?:ion)?[^\n]*required|call\s+["']?connect|no (?:active|connected)[^\n]*app)/iu.test(
      normalized,
    )
  ) {
    return "not-connected";
  }
  if (
    /(?:validation[^\n]*(?:failed|error)|failed[^\n]*validation|invalid[^\n]*\.pa\.yaml|(?:yaml|formula|source)[^\n]*(?:invalid|parse error)|compile[^\n]*(?:failed|error))/iu.test(
      normalized,
    )
  ) {
    return "validation-failed";
  }
  if (
    /(?:\b403\b|\bforbidden\b|does not have[^\n]*(?:permission|role)|insufficient privileges|license[^\n]*(?:missing|required)|not licensed|access denied)/iu.test(
      normalized,
    )
  ) {
    return "forbidden";
  }
  return "tool";
}

function isTransientWriteFailure(diagnostic: string | undefined): boolean {
  if (!diagnostic) return false;
  return /(?:\b429\b|\b5\d\d\b|too many requests|rate limit|service unavailable|gateway timeout|internal server error)/iu.test(
    diagnostic,
  );
}

function classifiedToolError(
  toolName: CanvasAuthoringToolName,
  category: CanvasAuthoringErrorCategory,
  diagnostic: string | undefined,
): CanvasAuthoringError {
  return new CanvasAuthoringError(categoryMessage(category, toolName), category, {
    toolName,
    diagnostic,
  });
}

function categoryMessage(
  category: CanvasAuthoringErrorCategory,
  toolName?: CanvasAuthoringToolName,
): string {
  switch (category) {
    case "runtime-unavailable":
      return "The Canvas authoring runtime is unavailable; install .NET SDK 10 and ensure dnx is on PATH.";
    case "package-unavailable":
      return "The pinned Canvas authoring package is unavailable from NuGet.";
    case "consent-missing":
      return "Canvas authoring authentication is missing required tenant consent.";
    case "tenant-mismatch":
      return "The selected Canvas app and signed-in account belong to different tenants.";
    case "unauthenticated":
      return "Canvas authoring is not authenticated; connect again with broker or browser authentication.";
    case "forbidden":
      return "Canvas authoring access was denied; verify the account's Power Apps license, role, and app permissions.";
    case "app-not-found":
      return "The requested Canvas app was not found in the selected environment.";
    case "coauthoring-unavailable":
      return "Canvas authoring requires coauthoring to be enabled for the app.";
    case "session-inactive":
      return "The Power Apps Studio coauthoring session is inactive; keep the app tab open and reconnect.";
    case "not-connected":
      return "Canvas authoring is not connected to an app; call connect again.";
    case "validation-failed":
      return "Canvas source validation failed; inspect the diagnostic and fix the .pa.yaml source before compiling again.";
    case "write-outcome-unknown":
      return "The Canvas compile outcome is unknown; do not retry automatically. Reconnect and inspect the live app state first.";
    case "data-source-not-found":
      return "The requested Canvas data source was not found; add it in Power Apps Studio, then reconnect.";
    case "timeout":
      return toolName
        ? `Canvas authoring tool "${toolName}" timed out.`
        : "Canvas authoring MCP startup timed out.";
    case "transport":
      return "The Canvas authoring MCP child connection was lost.";
    case "startup":
      return "The Canvas authoring MCP child could not be started.";
    case "tool":
      return toolName
        ? `Canvas authoring tool "${toolName}" failed.`
        : "The Canvas authoring operation failed.";
    case "validation":
      return "Canvas authoring input validation failed.";
    case "closed":
      return "The Canvas authoring client has already been closed.";
    case "contract":
      return "The Canvas authoring child contract is incompatible with this client.";
  }
}

function isTimeoutFailure(error: unknown): boolean {
  if (errorCode(error) === MCP_REQUEST_TIMEOUT) return true;
  if (!(error instanceof Error)) return false;
  return (
    error.name === "TimeoutError" ||
    /(?:timed? out|request timeout|timeout exceeded)/iu.test(error.message)
  );
}

function errorCode(error: unknown): number | undefined {
  const record = asRecord(error);
  return typeof record?.["code"] === "number" ? record["code"] : undefined;
}

function resultDiagnostic(result: CanvasAuthoringToolResult): string | undefined {
  const text = result.content
    .filter(
      (content): content is Extract<(typeof result.content)[number], { type: "text" }> =>
        content.type === "text",
    )
    .map((content) => content.text)
    .join("\n");
  return text ? sanitizeDiagnostic(text) : undefined;
}

function errorDiagnostic(error: unknown): string | undefined {
  if (error instanceof Error && error.message) {
    return sanitizeDiagnostic(error.message);
  }
  const record = asRecord(error);
  if (typeof record?.["message"] === "string") {
    return sanitizeDiagnostic(record["message"]);
  }
  return typeof error === "string" ? sanitizeDiagnostic(error) : undefined;
}

function sanitizeDiagnostic(value: string): string {
  return value
    .replace(ANSI_ESCAPE, "")
    .replace(/\bhttps?:\/\/[^\s"'<>]+/giu, "[REDACTED_URL]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu, "[REDACTED_EMAIL]")
    .replace(
      /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/gu,
      "[REDACTED_TOKEN]",
    )
    .replace(
      /\b(access[_ -]?token|refresh[_ -]?token|id[_ -]?token|authorization|password|secret|device[_ -]?code|login[_ -]?hint)\b\s*[:=]\s*[^\s,;]+/giu,
      "$1=[REDACTED]",
    )
    .slice(-MAX_DIAGNOSTIC_CHARS);
}
